import React, { useEffect, useState } from "react";
import { FiAlignJustify } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { RiMoonFill, RiSunFill } from "react-icons/ri";
import { useAuth } from "api/hooks/useAuth";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { userFullname, userProfileImage } from "store/user/userSelectors";
import { SearchInput } from "widgets/shared/inputs/search";
import { deviceGlobalSearch } from "api/device";
import GlobalSearchDialog from "widgets/dialogs/globalSearch";
import { DeviceGlobalSearchRes } from "api/types/device";
import CircleButton from "widgets/controls/buttons/circle-button";
import { MdArrowBack } from "react-icons/md";
import { usePermissions } from "hooks/usePermissions";
import { CONST } from "models/constants";
import { useBackBlockerContext } from "providers/back-blocker-provider";
import { useNotificationDialog } from "providers/notification-dialog-provider";
import { Popover, PopoverContent, PopoverTrigger } from "@chakra-ui/popover";
import { useDisclosure } from "@chakra-ui/hooks";
import useWhiteLabel from "whitelabel/useWhiteLabel";
import { resetPage } from "store/page/pageSlice";
import { isDarkMode, setDarkMode } from "util/theme";
import { useDefaultImages } from "providers/default-image-provider";
import useViewportDimensions from "hooks/viewport-dimensions";
import {
  SIDEBAR_WIDTH,
  DOCUMENT_PADDING,
  NAVBAR_HEIGHT,
} from "hooks/viewport-dimensions";
import { useTranslation } from "react-i18next";

const Navbar = (props: {
  onOpenSidenav: () => void;
  brandText: string;
  secondary?: boolean | string;
  navbarType?: string;
  [x: string]: any;
}) => {
  const {
    onOpenSidenav,
    brandText,
    navbarType = null,
    mini,
    setMini,
    // theme,
    // setTheme,
    hovered,
  } = props;
  const { t } = useTranslation();
  const { defaultAvatar } = useDefaultImages();
  const backBlocker = useBackBlockerContext();
  const { notify } = useNotificationDialog();
  const avatar = useSelector(userProfileImage, shallowEqual);
  const [darkmode, setDarkmode] = React.useState(isDarkMode());
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { canShow } = usePermissions();
  const { images, applyThemeColors } = useWhiteLabel();
  const { isGreaterThanLarge } = useViewportDimensions();

  const fullname = useSelector(userFullname, shallowEqual);
  const [filter, setFilter] = useState("");
  const [device, setDevice] = useState<DeviceGlobalSearchRes>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { onOpen, onClose, isOpen: isOpenMenu } = useDisclosure();
  const { signOut, setNeedsAccountSetup } = useAuth();

  const logout = () => {
    signOut();
  };

  const doSwitchAccount = async () => {
    localStorage.removeItem("accountContext");
    setNeedsAccountSetup(true);
    dispatch(resetPage());
  };

  const fetchData = async () => {
    const { data, status } = await deviceGlobalSearch(filter);

    if (status && status === 200) {
      setDevice(data);
      setIsOpen(true);
    }
  };

  useEffect(() => {
    if (filter && filter.length > 0) {
      fetchData();
    }
  }, [filter]);

  // Derive mini state automatically: collapse when not hovered on large screens
  useEffect(() => {
    if (typeof setMini !== "function") return;
    if (isGreaterThanLarge) {
      setMini(false);
    } else {
      // On smaller screens, keep sidebar in full mode
      setMini(true);
    }
  }, [hovered, isGreaterThanLarge, setMini]);

  // Apply theme colors when darkmode changes
  useEffect(() => {
    applyThemeColors(darkmode);
  }, [darkmode, applyThemeColors]);

  const getBgImage = () => {
    switch (navbarType) {
      case "bg1":
        return images.BG_1;
      case "bg2":
        return images.BG_2;
      case "bg3":
        return images.BG_3;
      case "bg4":
        return images.BG_4;
      case "bg5":
        return images.BG_5;
      case "bg6":
        return images.BG_6;
      default:
        return images.BG_1;
    }
  };

  return (
    <>
      {isOpen && (
        <GlobalSearchDialog
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
          }}
          result={device}
        />
      )}
      {navbarType && (
        <div
          id="main-content-navbar"
          className={`duration-175 fixed  h-72 items-center justify-between rounded-[20px] bg-cover bg-center pb-2 pl-2 pt-2 transition-all`}
          style={{
            left: `${
              isGreaterThanLarge
                ? SIDEBAR_WIDTH + DOCUMENT_PADDING
                : DOCUMENT_PADDING
            }px`,
            right: `${DOCUMENT_PADDING}px`,
            top: `${DOCUMENT_PADDING}px`,
            backgroundImage: `url(${getBgImage()})`,
          }}
        />
      )}
      <nav
        id="main-content-navbar"
        className={`duration-175 fixed z-[40] flex items-center justify-between rounded-[20px] pb-2 pl-2 pt-2 transition-all ${
          navbarType ? "p-2" : "bg-white/60 p-2 backdrop-blur-xl"
        }`}
        style={{
          height: `${NAVBAR_HEIGHT}px`,
          left: `${
            isGreaterThanLarge
              ? SIDEBAR_WIDTH + DOCUMENT_PADDING
              : DOCUMENT_PADDING
          }px`,
          right: `${DOCUMENT_PADDING}px`,
          top: `${DOCUMENT_PADDING}px`,
          // background: "linear-gradient(90deg, #ffffff30, transparent)",
        }}
      >
        <div className="ml-[6px] flex items-center gap-1">
          {navbarType && (
            <CircleButton
              icon={MdArrowBack}
              variant="ghost"
              color="white"
              size="md"
              onClick={() => {
                if (backBlocker?.shouldBlockBack?.()) {
                  notify({
                    header: t("notification.unsaved.changes.title"),
                    message: t("notification.unsaved.changes.description"),
                    okText: t("notification.unsaved.changes.leave"),
                    cancelText: t("button.action.stay"),
                    onOk: () => navigate(-1),
                    onCancel: () => {},
                  });
                } else {
                  navigate(-1);
                }
              }}
            />
          )}
          <p className="shrink ">
            <div
              className={`heading-lg ${
                navbarType ? "text-white" : "text-primary"
              }`}
            >
              {brandText}
            </div>
          </p>
        </div>

        <div className="flex items-center justify-end rounded-full bg-white px-3 py-2">
          {canShow(CONST.ROLE_ADMINISTRATOR) && mini === false && (
            <div className="bg-lightPrimary flex h-full items-center rounded-full xl:w-[225px]">
              <SearchInput
                sizes="md"
                onChange={(filters) => {
                  setFilter(filters);
                }}
                type="text"
                value={filter}
                extra="rounded-full"
                rounded="rounded-full"
              />
            </div>
          )}

          {mini === true && (
            <CircleButton
              icon={FiAlignJustify}
              variant="ghost"
              color="primary"
              size="md"
              onClick={onOpenSidenav}
            />
          )}

          <CircleButton
            icon={darkmode ? RiSunFill : RiMoonFill}
            variant="ghost"
            color="primary"
            size="md"
            onClick={() => {
              const newDarkMode = !darkmode;
              setDarkMode(newDarkMode);
              setDarkmode(newDarkMode);
              // Apply theme colors immediately
              applyThemeColors(newDarkMode);
            }}
          />

          {/* Profile & Dropdown */}

          <Popover
            placement={"left-end"}
            isOpen={isOpenMenu}
            onOpen={() => {
              onOpen();
            }}
            onClose={onClose}
          >
            <PopoverTrigger>
              <button>
                <img
                  className="h-10 w-10 rounded-full"
                  src={avatar || (defaultAvatar as string)}
                  alt="Elon Musk"
                />
              </button>
            </PopoverTrigger>

            <PopoverContent
              bg="white"
              className="max-h-60 w-max min-w-52 rounded-xl border px-2 pb-4"
            >
              <div>
                <div className="ml-4 mt-3">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-primary">
                      👋 Hey, {fullname}
                    </p>{" "}
                  </div>
                </div>
                <div className="mt-3 h-px w-full bg-gray-200" />

                <div className="ml-4 mt-3 flex flex-col">
                  <a
                    onClick={() => {
                      doSwitchAccount();
                    }}
                    className="cursor-pointer text-sm text-secondary"
                  >
                    {t("button.action.switch.accounts")}
                  </a>
                  <a
                    onClick={() => {
                      onClose();
                      navigate("/admin/profile");
                    }}
                    className="mt-3 cursor-pointer text-sm text-secondary"
                  >
                    {t("button.action.profile.settings")}
                  </a>
                  <a
                    onClick={logout}
                    className="mt-3 cursor-pointer text-sm font-medium text-red-600 hover:text-red-500"
                  >
                    {t("button.action.log.out")}
                  </a>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
